import React, { FC, memo } from 'react';

import { Tag, Tooltip } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import appConfig from '@/config/env';
import { useStores } from '@/components/hooks';
import { FileActionType } from '../../FileAction/menuItems';
import { getHex } from '../helpers.js';
import styles from './PolicyTag.module.scss';

export interface PolicyTagProps {
    title: string;
    fid?: string;
    handleMenuClick?: (action: FileActionType, fid: string) => void;
    policyClickAction?: FileActionType;
    isLocked?: boolean;
    showTooltip?: boolean;
}

const PolicyTag: FC<PolicyTagProps> = ({
    title = 'Default',
    handleMenuClick,
    fid,
    policyClickAction = 'set_policy',
    isLocked = false,
    showTooltip = false,
}) => {
    const { t } = useTranslation();
    const { sharedUsersStore } = useStores();
    const sharedUsers = fid ? sharedUsersStore.getFileSharedUsersList(fid) : [];
    const isPolicyChangeBlocked = appConfig.DISABLE_POLICY_CHANGES && !!sharedUsers?.length;
    const canChangePolicy = !!handleMenuClick && !isPolicyChangeBlocked;

    if (isLocked) {
        const lockedContent = (
            <span
                className={classNames(styles['locked-wrapper'], { [styles.clickable]: canChangePolicy })}
                onClick={canChangePolicy ? () => handleMenuClick(policyClickAction, fid) : undefined}
            >
                <Tag className={classNames(styles.tag, styles.locked)}>
                    {title}
                </Tag>
                <LockOutlined className={styles['lock-icon']} />
            </span>
        );

        if (showTooltip) {
            return (
                <Tooltip
                    placement="top"
                    color="#091F56"
                    title={t('uploadFiles.successTable.triggeredRule')}
                >
                    {lockedContent}
                </Tooltip>
            );
        }

        return lockedContent;
    }

    return (
        <Tag
            color={getHex(title)}
            title={title}
            className={classNames(styles.tag, { [styles.clickable]: canChangePolicy })}
            onClick={canChangePolicy ? () => handleMenuClick(policyClickAction, fid) : undefined}
            data-clickable={canChangePolicy}
            data-testid="sharedWithMe_table_filePolicy"
        >
            {title}
        </Tag>
    );
};

export default memo(PolicyTag);
